import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useSignal, Signal } from "../signals";
import { ValidationOptionsModel } from "../models/ValidationOptionsModel";
import { ValidationResultModel } from "../models/ValidationResultModel";
import {
    getSettingBoolean,
    hasSetting,
    getSettingString,
} from "../models/utilities/settingUtilities";
import { QuestionType } from "../models/enums/QuestionType";
import {
    useMentionValues,
    usePickMentions,
    useSetVariableMentions,
    useGetVariableMentions,
} from "./useMentions";
import { isQuestionSoftPrompt } from "../models/utilities/questionUtilities";
import { ValidationBuilder } from "../models/builders/ValidationBuilder";
import { translate } from "../models/utilities/translationUtilities";
import { QuestionModel } from "../models/QuestionModel";
import { useLanguage } from "./useLanguage";
import useQuestionnaire from "./useQuestionnaire";
import { QuestionnaireModel } from "../models/QuestionnaireModel";
import { VariableModel } from "../models/VariableModel";
import useRecall from "./useRecall";
import { isInputRequired } from "../models/utilities/questionnaireUtilities";
import { useThunkDispatch } from "../redux/thunks";
import { StoreState } from "../models/states/StoreState";
// eslint-disable-next-line import/no-deprecated
import { supportValidateSoftPrompt } from "../redux/thunks/validation";
import { isQuestionValidatedServerSide } from "../models/utilities/validationUtilities";

export function isAnswerRequired(question: QuestionModel, questionnaire: QuestionnaireModel) {
    if (question.type === QuestionType.Description) {
        return false;
    }
    if (!getSettingBoolean(questionnaire, "INPUT_REQUIRED")) {
        return false;
    }
    const answerRequired =
        !hasSetting(question, "AnswerRequired") || getSettingBoolean(question, "AnswerRequired");
    const control = !answerRequired ? question : question.parent || question;
    return (
        !hasSetting(control, "AnswerRequired") ||
        getSettingBoolean(control, "AnswerRequired", "Yes", "SoftPrompt", "UniquePrompt")
    );
}

type ValidatorCallback = (options: ValidationOptionsModel) => ValidationResultModel;

export const composeValidators = (...validators: ValidatorCallback[]) => (
    opt: ValidationOptionsModel,
) => validators.reduce((r, v) => (!r.isValid ? r : v(opt)), new ValidationBuilder().build());

function useCustomErrorMessage(question: QuestionModel) {
    const [language] = useLanguage();

    return React.useMemo(() => {
        const { parent } = question;
        if (hasSetting(question, "CustomErrorMessage"))
            return translate(question, "CustomErrorMessage", language);
        if (!parent) return null;
        if (parent.type === QuestionType.StarRatingGrid)
            return translate(parent.columns[0], "CustomErrorMessage", language);
        return translate(parent, "CustomErrorMessage", language);
    }, [question, language]);
}

/**
 * Answer required validator makes sure there is at least one mention for a variable.
 */
export function useAnswerRequiredValidator(
    question: QuestionModel,
    variable: VariableModel,
): ValidatorCallback {
    const questionnaire = useQuestionnaire();
    const getMentions = useGetVariableMentions();
    const state = useSelector((s: StoreState) => s);
    const dispatch = useThunkDispatch();

    const isSoftPrompt = isInputRequired(questionnaire) && isQuestionSoftPrompt(question);
    const customError = useCustomErrorMessage(question);

    return () => {
        const isAnswerComplete = getMentions(variable.name).some(
            ({ value }) => value !== undefined && value !== null && value.length > 0,
        );
        const bypassClientValidation = isQuestionValidatedServerSide(
            question.type,
            state?.interview.useClientValidation,
        );
        if (!getSettingBoolean(questionnaire, "INPUT_REQUIRED"))
            return new ValidationBuilder().build();
        if (!isAnswerComplete && isAnswerRequired(question, questionnaire)) {
            if (bypassClientValidation) {
                return new ValidationBuilder().build();
            }
            // Validate soft-prompt.
            if (isSoftPrompt) {
                const variables = [variable];
                // eslint-disable-next-line import/no-deprecated
                return supportValidateSoftPrompt(question, variables, dispatch, state);
            }
            // Question is required but has no mentions.
            return new ValidationBuilder()
                .fail(variable.name, customError || "IncompleteAnswer")
                .build();
        }
        // Question is valid.
        return new ValidationBuilder().build();
    };
}

/**
 * Discrete answer validator makes sure there is at least one valid choice for a variable
 * and checks for minimum and maximum selected choices.
 */
export function useDiscreteAnswerValidator(
    question: QuestionModel,
    variable: VariableModel,
    showSoftPrompt: boolean,
): ValidatorCallback {
    const state = useSelector((s: StoreState) => s);
    const [mentions, setMentions] = useMentionValues(variable.name);
    const min = parseInt(useRecall(getSettingString(question, "MinRequiredAnswers")), 10);
    const max = parseInt(useRecall(getSettingString(question, "MaxAllowedAnswers")), 10);
    const softPromptCode = getSettingString(question, "SoftPromptChoiceCode");
    const getMentions = useGetVariableMentions();

    React.useEffect(() => {
        const choiceValues = variable.choiceList.map((c) => c.value);
        const shouldRemove = (m) => !choiceValues.includes(m);
        if (mentions.some(shouldRemove)) setMentions((b) => b.remove(shouldRemove));
    }, [question, mentions, setMentions, variable.name, variable.choiceList, softPromptCode]);

    return composeValidators(
        useAnswerRequiredValidator(question, variable),
        function discreteAnswerValidator() {
            const bypassClientValidation = isQuestionValidatedServerSide(
                question.type,
                state?.interview.useClientValidation,
            );
            if (bypassClientValidation) {
                return new ValidationBuilder().build();
            }
            const currentMentions = getMentions(variable.name);
            const isChoiceVisible = (value: string) =>
                variable.choiceList?.some((c) => c.value === value);
            const isValidMention = (m) => m && m.value.length && isChoiceVisible(m.value);
            if (currentMentions.some(isValidMention)) {
                // Test for minimum and maximum values.
                if (min !== null && min > 0 && currentMentions.length < min) {
                    return new ValidationBuilder()
                        .fail(question.name, "InvalidChoicesMin", { min: String(min) })
                        .build();
                }
                if (max !== null && max > 0 && currentMentions.length > max) {
                    return new ValidationBuilder()
                        .fail(question.name, "InvalidChoicesMax", { max: String(max) })
                        .build();
                }
            }
            return new ValidationBuilder().build();
        },
    );
}

/**
 * A variadic validator validates that any of the variables have an answer and that
 * each of their mentions match a valid choice. In a variadic question minimum and
 * maximum applies to how many variables have an answer.
 */
export function useDiscreteVariadicValidator(
    question: QuestionModel,
    variables: VariableModel[],
): ValidatorCallback {
    const mentionsByVariable = usePickMentions(
        useMemo(() => variables.map((v) => v.name), [variables]),
    );
    const questionnaire = useQuestionnaire();
    const min = parseInt(useRecall(getSettingString(question, "MinRequiredAnswers")), 10);
    const max = parseInt(useRecall(getSettingString(question, "MaxAllowedAnswers")), 10);
    const setMentions = useSetVariableMentions();
    const getMentions = useGetVariableMentions();
    const state = useSelector((s: StoreState) => s);
    const customError = useCustomErrorMessage(question);
    const dispatch = useThunkDispatch();

    React.useEffect(() => {
        variables.forEach((variable) => {
            const choiceValues = variable.choiceList.map((c) => c.value);
            const shouldRemove = (m) => !choiceValues.includes(m);
            const mentions = mentionsByVariable[variable.name] || [];
            const mentionValues = mentions.map((m) => m.value);
            if (mentionValues.some(shouldRemove))
                setMentions(variable.name, (b) => b.remove(shouldRemove));
        });
    }, [mentionsByVariable, setMentions, variables]);

    return function discreteVariadicValidator() {
        const isChoiceVisible = (value: string) =>
            variables[0]?.choiceList?.some((c) => c.value === value);
        const isValidMention = (m) => m && m.value.length && isChoiceVisible(m.value);
        const answeredVariables = variables.filter(({ name }) =>
            getMentions(name).some(isValidMention),
        );
        const answerRequired = isAnswerRequired(question, questionnaire);
        const isSoftPrompt = answerRequired && isQuestionSoftPrompt(question);
        const failAllVariables = (key: string, args?: { [name: string]: string }) => {
            const result = new ValidationBuilder();
            variables.forEach(({ name }) => result.fail(name, key, args));
            return result.build();
        };
        const bypassClientValidation = isQuestionValidatedServerSide(
            question.type,
            state?.interview.useClientValidation,
        );
        if (!answeredVariables.length && answerRequired) {
            if (bypassClientValidation) {
                return new ValidationBuilder().build();
            }
            // Validate soft-prompt.
            if (isSoftPrompt) {
                // eslint-disable-next-line import/no-deprecated
                return supportValidateSoftPrompt(question, variables, dispatch, state);
            }
            // Question is required but has no mentions.
            return failAllVariables(customError || "IncompleteAnswer");
        }
        if (answeredVariables.length && !bypassClientValidation) {
            // Test for minimum and maximum values.
            if (min !== null && min > 0 && answeredVariables.length < min) {
                return failAllVariables("InvalidChoicesMin", { min: String(min) });
            }
            if (max !== null && max > 0 && answeredVariables.length > max) {
                return failAllVariables("InvalidChoicesMax", { max: String(max) });
            }
        }
        return new ValidationBuilder().build();
    };
}

export function useValidation(
    signal: Signal<ValidationOptionsModel, ValidationResultModel>,
    validator?: ValidatorCallback,
) {
    useSignal(signal, validator);
}
